import { useCallback, useEffect, useMemo } from 'react';
import {
  StripeRegisterTerminalComponents,
  RegisterTerminalModalStripeSteps,
  STRIPE_REGISTER_TERMINAL_STEPS_TITLES,
  RegisterTerminalModalJustifiSteps,
} from '@frontend/payments-register-terminal';
import { useMultiStepControl } from '@frontend/design-system';
import { useCollectPaymentMultiContext } from '../../../collect-payment-multi.context';
import { RegisterTerminalNavigationSwitch } from './register-terminal-navigation-switch';
import { RegisterTerminalStepBodyProps } from './register-terminal.types';
import { RegisterTerminalStepper } from './stepper/register-terminal-stepper';

export const RegisterTerminalStripe = () => {
  return <RegisterTerminalNavigationSwitch body={RegisterTerminalStripeBody} />;
};

const RegisterTerminalStripeBody = ({
  onGoBack,
  onCancel,
  onComplete,
  onRegisterTerminal,
}: RegisterTerminalStepBodyProps) => {
  const { setMinWidth, resetMinWidth } = useCollectPaymentMultiContext();

  const {
    stripeData: { stripeLocationId },
    activeLocationId,
  } = useCollectPaymentMultiContext();

  const multiStepControl = useMultiStepControl({
    onComplete: () => {
      onComplete();
    },
    onCancel: () => {
      onCancel();
    },
  });

  const childGoBack = useCallback(() => {
    multiStepControl.goPrevStep();
  }, [multiStepControl.goPrevStep]);

  const goToChildStep = useCallback(
    (step: RegisterTerminalModalJustifiSteps | RegisterTerminalModalStripeSteps) => {
      multiStepControl.goToStep({ orStep: step });
    },
    [multiStepControl.goToStep]
  );

  useEffect(() => {
    setMinWidth(776);

    return () => {
      resetMinWidth();
    };
  }, []);

  const steps = useMemo(() => {
    return [
      {
        id: RegisterTerminalModalStripeSteps.InstallBattery,
        title: STRIPE_REGISTER_TERMINAL_STEPS_TITLES[RegisterTerminalModalStripeSteps.InstallBattery].heading,
        stepperLabel: STRIPE_REGISTER_TERMINAL_STEPS_TITLES[RegisterTerminalModalStripeSteps.InstallBattery].title,
        component: <StripeRegisterTerminalComponents.InstallBattery goBack={onGoBack} goToStep={goToChildStep} />,
      },
      {
        id: RegisterTerminalModalStripeSteps.PlugInTerminal,
        parentId: RegisterTerminalModalStripeSteps.InstallBattery,
        title: STRIPE_REGISTER_TERMINAL_STEPS_TITLES[RegisterTerminalModalStripeSteps.PlugInTerminal].heading,
        stepperLabel: STRIPE_REGISTER_TERMINAL_STEPS_TITLES[RegisterTerminalModalStripeSteps.PlugInTerminal].title,
        component: <StripeRegisterTerminalComponents.PlugInTerminal goBack={childGoBack} goToStep={goToChildStep} />,
      },
      {
        id: RegisterTerminalModalStripeSteps.TurnTerminalOn,
        parentId: RegisterTerminalModalStripeSteps.InstallBattery,
        title: STRIPE_REGISTER_TERMINAL_STEPS_TITLES[RegisterTerminalModalStripeSteps.TurnTerminalOn].heading,
        stepperLabel: STRIPE_REGISTER_TERMINAL_STEPS_TITLES[RegisterTerminalModalStripeSteps.TurnTerminalOn].title,
        component: <StripeRegisterTerminalComponents.TurnTerminalOn goBack={childGoBack} goToStep={goToChildStep} />,
      },
      {
        id: RegisterTerminalModalStripeSteps.ConnectToNetwork,
        title: STRIPE_REGISTER_TERMINAL_STEPS_TITLES[RegisterTerminalModalStripeSteps.ConnectToNetwork].heading,
        stepperLabel: STRIPE_REGISTER_TERMINAL_STEPS_TITLES[RegisterTerminalModalStripeSteps.ConnectToNetwork].title,
        component: <StripeRegisterTerminalComponents.ConnectToNetwork goBack={childGoBack} goToStep={goToChildStep} />,
      },
      {
        id: RegisterTerminalModalStripeSteps.GeneratePairingCode,
        title: STRIPE_REGISTER_TERMINAL_STEPS_TITLES[RegisterTerminalModalStripeSteps.GeneratePairingCode].heading,
        stepperLabel: STRIPE_REGISTER_TERMINAL_STEPS_TITLES[RegisterTerminalModalStripeSteps.GeneratePairingCode].title,
        component: (
          <StripeRegisterTerminalComponents.GeneratePairingCode goBack={childGoBack} goToStep={goToChildStep} />
        ),
      },
      {
        id: RegisterTerminalModalStripeSteps.PairYourTerminal,
        parentId: RegisterTerminalModalStripeSteps.GeneratePairingCode,
        title: STRIPE_REGISTER_TERMINAL_STEPS_TITLES[RegisterTerminalModalStripeSteps.PairYourTerminal].heading,
        stepperLabel: STRIPE_REGISTER_TERMINAL_STEPS_TITLES[RegisterTerminalModalStripeSteps.PairYourTerminal].title,
        component: (
          <StripeRegisterTerminalComponents.PairYourTerminal
            goBack={childGoBack}
            goToStep={goToChildStep}
            onSuccess={onComplete}
            onTerminalPaired={onRegisterTerminal}
            locationId={activeLocationId}
            stripeLocationId={stripeLocationId}
          />
        ),
      },
    ];
  }, [onRegisterTerminal, childGoBack, onGoBack, goToChildStep]);

  return <RegisterTerminalStepper steps={steps} multiStepControl={multiStepControl} />;
};
